/* eslint-disable max-len */
/**
 * @module M/plugin/SimpleAttributions
 */
import '../assets/css/simpleattributions';
import AttributionsImpl from '../../impl/ol/js/simpleattributions';
import SimpleAttributionsControl from './simpleattributionscontrol';

/**
 * @typedef {AttributionsOptions}
 *
 * The mode according to which the plugin will consult the simpleattributions.
 * @type {number}
 *
 */

/**
 * Class of simpleattributions plugin
 * @param {object}
 * @classdesc
 */
export default class SimpleAttributions extends M.Plugin {
  /**
   * @constructor
   * @extends {M.Plugin}
   * @param {AttributionsOptions} options
   * @api
   */
  constructor(options = {}) {
    super();

    /**
     * Facade of the map
     *
     * @private
     * @type {M.Map}
     */
    this.map_ = null;

    /**
     * Array of controls
     *
     * @private
     * @type {Array<M.Control>}
     */
    this.controls_ = [];

    /**
     * Minimum width of the view control
     * @private
     * @type {string}
     */
    this.minWidth_ = options.minWidth || '100px';

    /**
     * Minimum width of the view control
     * @private
     * @type {string}
     */
    this.maxWidth_ = options.maxWidth || '200px';

    /**
     * Position of the view control
     * @private
     * @type {string}
     */
    this.position_ = options.position || 'BL';

    /**
    * Label to show of the UI Plugin
    *
    * @private
    * @type {string}
    */
    this.label = options.label || '';

    window.addEventListener('resize', e => this.setCollapsiblePanel(e));
  }

  /**
   * This function adds this plugin into the map
   *
   * @public
   * @function
   * @param {M.Map} map the map to add the plugin
   * @api stable
   */
  addTo(map) {
    this.map_ = map;
    this.impl_ = new AttributionsImpl(map);
    this.control_ = new SimpleAttributionsControl(this.position_, this.closePanel, this.label);
    this.controls_.push(this.control_);

    this.panel_ = new M.ui.Panel('SimpleAttributions', {
      collapsible: window.innerWidth < 769,
      position: M.ui.position[this.position_],
      className: 'm-panel-simpleattributions',
      collapsedButtonClass: 'g-cartografia-info',
    });

    this.panel_.addControls(this.control_);

    this.map_.addPanels(this.panel_);

    this.map_.getMapImpl().once('postrender', (evt) => {
      this.changeAttributions();
    });
  }

  /**
   * This function destroys this plugin
   *
   * @public
   * @function
   * @api
   */
  destroy() {
    this.map_.removeControls([this.control_]);
    this.panel_ = null;
  }

  /**
   * This method shows the layer simpleattributions
   *
   * @function
   * @public
   */
  changeAttributions() {
    this.clearContent();
    this.setVisible(true);
    let mapAttributions = [];
    mapAttributions = [{ attribution: this.label }];
    this.addContent(mapAttributions);
  }

  /**
   * This method adds the text content to the view attribution
   *
   * @function
   * @public
   */
  addContent(simpleattributions) {
    const html = this.control_.getElement();
    const links = simpleattributions.map((attrOpt) => {
      const link = document.createElement('spam');
      link.target = '_blank';
      link.innerHTML = attrOpt.attribution;
      return link;
    });
    const div = document.createElement('div');
    links.forEach((link) => {
      div.append(link);
    });
    html.append(div);
  }

  /**
   * This method adds the text content to the view attribution
   *
   * @function
   * @public
   */
  clearContent() {
    if (!M.utils.isNullOrEmpty(this.control_)) {
      const html = this.control_.getElement();
      html.querySelectorAll('div').forEach(child => html.removeChild(child));
    }
  }

  /**
   * This method toggle de visibility of the view attribution
   */
  setVisible(visibility) {
    const html = this.control_.getElement();
    html.style.display = visibility === false ? 'none' : '';
  }

  /**
   * @function
   * @public
   */
  closePanel() {
    this.getPanel().collapse();
  }

  /**
   * @function
   * @public
   */
  changeContentAttribution(content) {
    this.control_.changeContent(content);
  }

  /**
   * @function
   * @public
   */
  setCollapsiblePanel(e) {
    if (this.getPanel() && this.getPanel().getTemplatePanel()) {
      if (e.target.innerWidth < 769) {
        this.getPanel().getTemplatePanel().classList.remove('no-collapsible');
        this.closePanel();
      } else {
        this.getPanel().getTemplatePanel().classList.add('no-collapsible');
        this.getPanel().getTemplatePanel().classList.remove('collapsed');
      }
    }
  }

  /**
   * @function
   * @public
   */
  getPanel() {
    return this.panel_;
  }

  /**
   * Name of the plugin
   *
   * @getter
   * @function
   */
  get name() {
    return 'simpleattributions';
  }

  /**
   * Position of the plugin
   *
   * @public
   * @function
   * @api
   */
  get position() {
    return this.position_;
  }
}
